import React, { PropTypes } from 'react';
import shallowCompare from 'react-addons-shallow-compare';
import moment from 'moment';
import 'moment-range';
import { List } from 'immutable';
import cx from 'classnames';

moment.locale('fr');

export const DATE_FORMAT = 'DD/MM/YYYY';

const refineMomentWeek = weekMoment => ({
  week: weekMoment.isoWeek(),
  month: moment()
    .month(weekMoment.month())
    .format('MMMM'),
  year: weekMoment.year(),
  date: weekMoment.format(DATE_FORMAT),
});

const getWeekList = () => {
  const start = moment()
    .startOf('month')
    .startOf('isoweek');
  const end = moment()
    .day('Monday')
    .startOf('month')
    .add(1, 'year')
    .endOf('month');
  const range = moment.range(start, end);

  const sortByYearAndWeek = month => {
    const monthValue =
      month[1][0].year + '' + ('0' + month[1][0].week).slice(-2);

    return parseInt(monthValue, 10);
  };

  return List(range.toArray('weeks'))
    .filter(week => week.isAfter(start))
    .map(week => refineMomentWeek(week))
    .groupBy(item => item.month + ' ' + item.year)
    .toMap()
    .map((k, v) => [v, k.toArray()])
    .sortBy((v, k) => sortByYearAndWeek(v));
};

const tryParseValue = value => {
  if (value && value.length) {
    const date = moment(value, DATE_FORMAT);
    return refineMomentWeek(date);
  }

  return null;
};

const mapValueToSelection = value => {
  // Checking for existing value (only single supported for now)
  if (Array.isArray(value)) {
    const grouped = List(value)
      .groupBy(v => v)
      .map((v, k) => Object.assign({ date: k, inter_number: v.size }))
      .valueSeq();
    const values = grouped
      .toArray()
      .map(v =>
        Object.assign({
          ...tryParseValue(v.date),
          inter_number: v.inter_number,
        })
      )
      .filter(v => v.week != null);
    return values;
  } else {
    const selected = [];
    const existing = tryParseValue(value);
    if (existing) {
      selected.push(existing);
    }
    return selected;
  }
};

// TODO: remove toArray()s and work with immutable lists
// TODO: Support multiple default values
class PreplanificationSelection extends React.Component {
  static propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    multiSelection: PropTypes.bool,
    multiIntervention: PropTypes.bool,
    onSelectionChanged: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      content: getWeekList().toArray(),
      selected: mapValueToSelection(props.value),
    };

    this.onClick = this.onClick.bind(this);
    this.onAdd = this.onAdd.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.value !== this.props.value) {
      this.setState({
        selected: mapValueToSelection(nextProps.value),
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  onClick(ct) {
    let newSelection;

    const current = List(this.state.selected);
    const exists = current.findIndex(
      item => item.week === ct.week && item.year === ct.year
    );

    if (exists !== -1) {
      newSelection =
        this.props && this.props.noDelete ? current : current.delete(exists);
    } else {
      if (this.props.multiSelection) {
        newSelection = current.push(ct);
      } else {
        newSelection = List([ct]);
      }
    }

    this._notifySelectionChanged(newSelection);

    this.setState({
      selected: newSelection.toArray(),
    });
  }

  onAdd(ct) {
    const current = List(this.state.selected);
    const newSelection = current.map(item => {
      if (item.week === ct.week && item.year === ct.year) {
        return { ...ct };
      } else {
        return item;
      }
    });

    this._notifySelectionChanged(newSelection);

    this.setState({
      selected: newSelection.toArray(),
    });
  }

  _notifySelectionChanged(newSelection) {
    const { multiSelection, onSelectionChanged } = this.props;
    if (multiSelection) {
      onSelectionChanged(newSelection.toArray());
    } else {
      onSelectionChanged(newSelection.first());
    }
  }

  render() {
    const { content } = this.state;

    return (
      <table className="ui definition table checkable">
        <tbody>
          {content.map(item => (
            <PreplanificationRow
              key={item[0]}
              rowName={item[0]}
              rowContent={item[1]}
              onWeekClicked={this.onClick}
              onAdd={this.onAdd}
              selected={this.state.selected}
              multiIntervention={this.props.multiIntervention}
            />
          ))}
        </tbody>
      </table>
    );
  }
}

const PreplanificationRow = ({
  rowName,
  rowContent,
  onWeekClicked,
  onAdd,
  multiIntervention,
  ...rest
}) => {
  const emptyCell = rowContent.length === 5 ? null : <td className="empty" />;

  return (
    <tr>
      <td>{rowName}</td>
      {rowContent.map(item => (
        <PreplanificationItem
          key={item.week}
          content={item}
          onClick={onWeekClicked}
          onAdd={onAdd}
          multiIntervention={multiIntervention}
          {...rest}
        />
      ))}
      {emptyCell}
    </tr>
  );
};

PreplanificationRow.propTypes = {
  rowName: PropTypes.string.isRequired,
  rowContent: PropTypes.array.isRequired,
  onWeekClicked: PropTypes.func.isRequired,
  selected: PropTypes.array.isRequired,
  noDelete: PropTypes.bool,
  multiIntervention: PropTypes.bool,
};

const PreplanificationItem = ({
  content,
  selected,
  onClick,
  onAdd,
  multiIntervention,
}) => {
  const isChecked = selected.find(
    item =>
      item.week === content.week &&
      item.year === content.year &&
      !item.month.localeCompare(content.month)
  );

  const interventionNumber = isChecked ? isChecked.inter_number : 0;

  const rowClassName = cx('center aligned', {
    checked: isChecked,
  });

  const onContentClick = evt => {
    onClick(content);
  };

  const onAddClick = evt => {
    evt.stopPropagation(true);
    evt.preventDefault(true);
    onAdd({
      ...content,
      inter_number: isChecked.inter_number ? isChecked.inter_number + 1 : 2,
    });
  };

  if (multiIntervention) {
    return (
      <td className={rowClassName} onClick={onContentClick}>
        <div className="ui grid">
          <div className="five wide column">
            {isChecked && (
              <i
                className="plus square inverted outline icon"
                onClick={onAddClick}
              />
            )}
          </div>
          <div className="six wide column">
            <div className="ui checkbox">
              <input type="checkbox" tabIndex="0" className="hidden" />
              <label>S{content.week}</label>
            </div>
          </div>
          <div className="five wide column">
            {isChecked && (
              <span style={{ color: 'white' }}>
                {interventionNumber > 1 && interventionNumber}
              </span>
            )}
          </div>
        </div>
      </td>
    );
  } else {
    return (
      <td className={rowClassName} onClick={onContentClick}>
        <div className="ui checkbox">
          <input type="checkbox" tabIndex="0" className="hidden" />
          <label>S{content.week}</label>
        </div>
      </td>
    );
  }
};

PreplanificationItem.propTypes = {
  content: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  selected: PropTypes.array.isRequired,
  multiIntervention: PropTypes.bool,
};

export default PreplanificationSelection;



// WEBPACK FOOTER //
// ./src/modules/common/components/PreplanificationSelection.js